import {useCallback, useMemo, useState} from 'react'
import {
  getVersions,
  getCurrentDefault
} from '../components/card-preview/preview-renderers'
import {Flags} from './use-flags'
import {upperCaseFirst} from '../lib/helpers'

export const useSdkVersionSelector = (orgId: string) => {
  const sdkVersionKey = `current-sdk-version-${orgId}`

  const versions = useMemo(() => getVersions(), [])
  const currentDefault = useMemo(() => getCurrentDefault('stable'), [])
  const storedSdkVersion = localStorage.getItem(sdkVersionKey)
  const [selectedSdkVersion, _setSelectedSdkVersion] = useState<string>()

  // find by selectedSdkVersion else storedSdkVersion else currentDefault
  const currentSdkVersion = useMemo(
    () =>
      versions?.find(t => t.id === selectedSdkVersion)?.id ??
      versions?.find(t => t.id === storedSdkVersion)?.id ??
      currentDefault,
    [currentDefault, selectedSdkVersion, storedSdkVersion, versions]
  )

  const setSelectedSdkVersion = useCallback(
    (versionId: string) => {
      _setSelectedSdkVersion(versionId)
      localStorage.setItem(
        sdkVersionKey,
        // unset value if default, because then it will update
        // automatically if the default changes
        versionId !== currentDefault ? versionId : ''
      )
    },
    [currentDefault, sdkVersionKey]
  )

  const getPreviewChooserSDKVersionOptions = useCallback(
    (type: string, flags: Flags) =>
      versions
        .filter(v => !v.preview || (v.preview && flags.featurePreview))
        .map(v => ({
          id: v.id,
          name: `${v.current ? 'Current' : ''} ${
            v.current ? v.type : upperCaseFirst(v.type)
          } release ${v.id}${v.preview ? ' (preview)' : ''}`,
          type
        })),
    [versions]
  )

  return {
    versions,
    currentSdkVersion,
    setSelectedSdkVersion,
    getPreviewChooserSDKVersionOptions
  }
}
